import React, { useState, useMemo, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from '../../../redux/TranslationHelper';
import { CompanyHistoryProps, HistoryItem } from './CompanyHistory.types';
import { adaptCompanyHistoryProps } from './CompanyHistory.adapter';
import './CompanyHistory.css';

/**
 * 회사 연혁 컴포넌트
 */
const CompanyHistoryComponent: React.FC<any> = (props) => {
  // 어댑터를 통해 props 변환
  const adaptedProps = useMemo(() => adaptCompanyHistoryProps(props), [props]);
  const { t } = useTranslation();
  
  const {
    title = t("회사 연혁"),
    subtitle,
    timelineStyle = 'vertical',
    colorScheme = {
      primary: '#3498db',
      secondary: '#f1c40f',
      background: '#ffffff',
      text: '#333333'
    },
    animation = true,
    imageWidth = 150,
    imageHeight = 150,
    historyItems = [],
    style = {},
    className = '',
  } = adaptedProps;

  // 타임라인 항목 추가
  const addHistoryItem = useCallback((item: HistoryItem) => {
    return [...historyItems, { ...item, id: uuidv4() }];
  }, [historyItems]);

  // 타임라인 항목 업데이트
  const updateHistoryItem = useCallback((id: string, updatedItem: Partial<HistoryItem>) => {
    return historyItems.map(item =>
      item.id === id ? { ...item, ...updatedItem } : item
    );
  }, [historyItems]);

  // 타임라인 항목 삭제
  const deleteHistoryItem = useCallback((id: string) => {
    return historyItems.filter(item => item.id !== id);
  }, [historyItems]);

  // 수직형 타임라인 렌더링
  const renderVerticalTimeline = useCallback(() => (
    <div className="company-history-timeline vertical">
      {historyItems.map((item, index) => {
        const isEven = index % 2 === 0;
        
        return (
          <div 
            key={item.id} 
            className={`timeline-item ${animation ? 'animate' : ''} ${isEven ? 'even' : 'odd'}`}
            style={{ animationDelay: `${index * 0.2}s` }}
          >
            {/* 왼쪽 영역: 짝수면 텍스트, 홀수면 이미지 */}
            <div className="timeline-left">
              {isEven ? (
                <div 
                  className="timeline-content text-right"
                  style={{ backgroundColor: colorScheme.background, borderColor: colorScheme.primary }}
                >
                  <h3 style={{ color: colorScheme.primary }}>{typeof item.title === 'string' ? t(item.title) : item.title}</h3>
                  <p style={{ color: colorScheme.text }}>{typeof item.description === 'string' ? t(item.description) : item.description}</p>
                </div>
              ) : (
                item.image && (
                  <div className="timeline-image" style={{ 
                    width: `${imageWidth}px`, 
                    height: `${imageHeight}px`,
                    padding: '5px',
                    backgroundColor: colorScheme.background,
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <img src={item.image} alt={typeof item.title === 'string' ? t(item.title) : String(item.title)} />
                  </div>
                )
              )}
            </div>
            
            {/* 중앙 연도 */}
            <div className="timeline-center">
              <div 
                className="timeline-year" 
                style={{ 
                  backgroundColor: colorScheme.primary, 
                  color: colorScheme.background,
                  position: 'relative',
                  zIndex: 4
                }}
              >
                {item.year}
              </div>
            </div>
            
            {/* 오른쪽 영역: 짝수면 이미지, 홀수면 텍스트 */}
            <div className="timeline-right">
              {isEven ? (
                item.image && (
                  <div className="timeline-image" style={{ 
                    width: `${imageWidth}px`, 
                    height: `${imageHeight}px`,
                    padding: '5px',
                    backgroundColor: colorScheme.background,
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <img src={item.image} alt={typeof item.title === 'string' ? t(item.title) : String(item.title)} />
                  </div>
                )
              ) : (
                <div 
                  className="timeline-content text-left"
                  style={{ backgroundColor: colorScheme.background, borderColor: colorScheme.primary }}
                >
                  <h3 style={{ color: colorScheme.primary }}>{typeof item.title === 'string' ? t(item.title) : item.title}</h3>
                  <p style={{ color: colorScheme.text }}>{typeof item.description === 'string' ? t(item.description) : item.description}</p>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  ), [historyItems, animation, colorScheme, imageWidth, imageHeight, t]);

  // 수평형 타임라인 렌더링
  const renderHorizontalTimeline = useCallback(() => (
    <div className="company-history-timeline horizontal">
      <div className="timeline-line" style={{ backgroundColor: colorScheme.primary }}></div>
      <div className="timeline-items-container">
        {historyItems.map((item, index) => (
          <div 
            key={item.id} 
            className={`timeline-item ${animation ? 'animate' : ''}`}
            style={{ 
              animationDelay: `${index * 0.2}s`,
              left: `${(index / (historyItems.length - 1 || 1)) * 100}%`
            }}
          >
            <div 
              className="timeline-point" 
              style={{ backgroundColor: colorScheme.secondary }}
            ></div>
            <div 
              className="timeline-year" 
              style={{ color: colorScheme.primary }}
            >
              {item.year}
            </div>
            
            {item.image && (
              <div className="timeline-image horizontal-image" style={{ 
                width: `${imageWidth}px`, 
                height: `${imageHeight}px`,
                padding: '5px',
                backgroundColor: colorScheme.background,
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <img src={item.image} alt={typeof item.title === 'string' ? t(item.title) : String(item.title)} />
              </div>
            )}
            
            <div 
              className="timeline-content"
              style={{ backgroundColor: colorScheme.background, borderColor: colorScheme.primary }}
            >
              <h3 style={{ color: colorScheme.primary }}>{typeof item.title === 'string' ? t(item.title) : item.title}</h3>
              <p style={{ color: colorScheme.text }}>{typeof item.description === 'string' ? t(item.description) : item.description}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  ), [historyItems, animation, colorScheme, imageWidth, imageHeight, t]);

  return (
    <div className={`company-history-component ${className}`} style={style}>
      <div className="history-header">
        <h2>{typeof title === 'string' ? t(title) : title}</h2>
        {subtitle && <p className="subtitle">{typeof subtitle === 'string' ? t(subtitle) : subtitle}</p>}
      </div>
      
      {timelineStyle === 'vertical' ? renderVerticalTimeline() : renderHorizontalTimeline()}
    </div>
  );
};

export default CompanyHistoryComponent; 